import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DefaultEditor_InputText, DefaultEditor_TextArea } from '@app/styles/frontend/ui-manager/ui-components';
import { AwaitModules, Component_GenericRenderer, ComponentSync, EditableDBItemV3, ModuleFE_Utils, TS_PropRenderer } from '@nu-art/thunderstorm-frontend';
import './Editor_Pathway.scss';
import { asArray } from '@nu-art/ts-common';
import { Fragment } from 'react';
import { ModuleFE_Pathway } from '../../../../../_entity.js';
import { Editor_PathwayDiseases, Editor_PathwayDiseases_Modules } from './inner-renderers/Editor_PathwayDiseases.js';
import { Editor_PathwayType } from './inner-renderers/Editor_PathwayType.js';
import { RendererKey_PathwayReview } from '@app/pathway-shared';
import { FadeScrollWrapper } from '@app/styles';
import { Renderer_PathwayVariables, Renderer_PathwayVariables_Modules } from '../shared/Renderer_PathwayVariables/Renderer_PathwayVariables.js';
import { Renderer_PathwayStates } from '../shared/Renderer_PathwayStates/Renderer_PathwayStates.js';
import { Renderer_PathwayTransitions } from '../shared/Renderer_PathwayTransitions/Renderer_PathwayTransitions.js';
import { Renderer_PathwayActions } from '../shared/Renderer_PathwayActions/Renderer_PathwayActions.js';
import { Renderer_PathwayScores } from '../shared/Renderer_PathwayScores/Renderer_PathwayScores.js';
import { Renderer_PathwayTags } from '../shared/Renderer_PathwayTags/Renderer_PathwayTags.js';
import { getPathwayHVOrgConflicts } from '../shared/hv-org-conflicts.js';
import { setPathwayHVOrgConflict } from '../shared/hv-org-conflict-guard.js';
import { HVOrgConflictBanner } from '../shared/HVOrgConflictBanner.js';
// Matches URLs starting with http(s)://. Trailing punctuation is trimmed below
// so periods/commas/closing brackets that end a sentence are not part of the link.
const URL_REGEX = /(https?:\/\/[^\s<>"']+)/gi;
const TRAILING_PUNCTUATION_REGEX = /[.,;:!?)\]}]+$/;
// Defence-in-depth on top of the regex: never render an <a> for anything that
// is not parsable as an http(s) URL — guards against javascript:/data: payloads
// in case the regex is ever loosened.
const isSafeHttpUrl = (raw) => {
    try {
        const parsed = new URL(raw);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    }
    catch {
        return false;
    }
};
const renderTextWithLinks = (text) => {
    if (!text)
        return [];
    // Capturing-group split: even indices are plain text, odd indices are URL matches.
    return text.split(URL_REGEX).map((segment, index) => {
        if (index % 2 === 0)
            return segment;
        const trailing = segment.match(TRAILING_PUNCTUATION_REGEX);
        const url = trailing ? segment.slice(0, -trailing[0].length) : segment;
        const tail = trailing?.[0] ?? '';
        if (!isSafeHttpUrl(url))
            return segment;
        return _jsxs(Fragment, { children: [_jsx("a", { href: url, target: '_blank', rel: 'noopener noreferrer', children: url }), tail] }, index);
    });
};
export class Editor_Pathway extends ComponentSync {
    // ######################## Life Cycle ########################
    __onPathwayUpdated = (...params) => {
        if (!this.mounted)
            return;
        const currentPathwayId = this.state.editable.item._id;
        const pathways = asArray(params[1]);
        if (pathways.find(pathway => pathway._id === currentPathwayId))
            this.forceUpdate();
    };
    deriveStateFromProps(nextProps, state) {
        state.editMode = nextProps.renderArgs.editMode;
        state.tabId = nextProps.tabId;
        if (nextProps.renderArgs.itemId !== state.editable?.item._id)
            state.editable = this.generateEditableItem(nextProps.renderArgs.itemId);
        return state;
    }
    // ######################## Logic ########################
    generateEditableItem(pathwayId) {
        const pathway = ModuleFE_Pathway.cache.unique(pathwayId);
        return new EditableDBItemV3(pathway, ModuleFE_Pathway)
            .setOnChanged(async () => this.forceUpdate())
            // Swallow save errors (e.g. the backend HV+org 409): the warning banner already
            // explains the conflict, so we just prevent the unhandled-rejection error overlay.
            .setOnError(async (_item, err) => this.logError('Failed saving pathway', err))
            .setAutoSave(true);
    }
    // ######################## Render ########################
    // Generic UI slot for the optional "Review" section (link that opens the imported HTML overlay).
    // Pathway owns the location; data-migration registers the component (so pathway never imports it).
    // Guarded: getRenderer throws when nothing is registered.
    renderReviewSlot = () => {
        if (!ModuleFE_Utils.renderers[RendererKey_PathwayReview])
            return null;
        return _jsx(Component_GenericRenderer, { rendererKey: RendererKey_PathwayReview, props: { tabId: this.state.tabId, pathwayId: this.props.renderArgs.itemId, editMode: this.state.editMode } }, this.props.renderArgs.itemId);
    };
    // Early UX mirror of the backend HV+org uniqueness rule (backend stays authoritative on save).
    renderHVOrgWarning = (conflicts) => _jsx(HVOrgConflictBanner, { conflicts: conflicts });
    // Clear the per-tab conflict flag when this editor leaves the DOM (tab closed, or a
    // state/transition editor takes over), so a stale flag can't block an unrelated toggle.
    componentWillUnmount() {
        setPathwayHVOrgConflict(this.state.tabId, false);
    }
    render() {
        const editable = this.state.editable;
        const editMode = this.props.renderArgs.editMode;
        // Compute once: drives the banner AND publishes the per-tab flag the header toggle reads,
        // so the block reflects the live (possibly unsaved) editable, not just cached data.
        const conflicts = getPathwayHVOrgConflicts(editable.item);
        setPathwayHVOrgConflict(this.state.tabId, conflicts.length > 0);
        return _jsxs(FadeScrollWrapper, { className: 'e__pathway', children: [this.renderHVOrgWarning(conflicts), _jsx(TS_PropRenderer.Horizontal, { label: 'Pathway Name:', children: _jsx(DefaultEditor_InputText, { editable: editable, prop: 'label', disabled: !editMode }) }), _jsx(TS_PropRenderer.Horizontal, { label: 'Pathway Type:', children: _jsx(Editor_PathwayType, { pathway: editable, editMode: editMode }) }), _jsx(AwaitModules, { modules: Editor_PathwayDiseases_Modules, children: _jsx(TS_PropRenderer.Vertical, { label: 'Associated Diseases', children: _jsx(Editor_PathwayDiseases, { pathway: editable, editMode: editMode }) }) }), _jsx(TS_PropRenderer.Vertical, { label: 'Reference', children: editMode
                        ? _jsx(DefaultEditor_TextArea, { editable: editable, prop: 'description', disabled: false })
                        : _jsx("div", { className: 'e__pathway__reference-text', children: renderTextWithLinks(editable.item.description ?? '') }) }), this.renderReviewSlot(), _jsx(Renderer_PathwayScores, { pathway: editable, editMode: editMode }), _jsx(Renderer_PathwayTags, { pathway: editable, editMode: editMode }), _jsx(Renderer_PathwayStates, { workHubItem: this.props.workHubItem, pathwayId: this.props.renderArgs.itemId, tabId: this.state.tabId }), _jsx(Renderer_PathwayTransitions, { workHubItem: this.props.workHubItem, tabId: this.state.tabId, pathwayId: this.props.renderArgs.itemId }), _jsx(Renderer_PathwayActions, { pathwayId: this.props.renderArgs.itemId }), _jsx(AwaitModules, { modules: Renderer_PathwayVariables_Modules, children: _jsx(Renderer_PathwayVariables, { pathwayId: editable.item._id }) })] });
    }
}
//# sourceMappingURL=Editor_Pathway.js.map